<template>
  <div class="address-info">
    <div class="md:mb-5">
      <tml-input
        v-if="actAsBilling && !lockCustomer"
        v-model="addressInfo.email"
        :field-name="`${fieldNamePrefix}email`"
        placeholder="Email"
        type="email"
        :validation-rules="{required: true, email: true}"
        @blur="onBlur"
      />

      <tml-toggle
        v-if="actAsBilling && !lockCustomer && showMailOptIn"
        :model-value="mailOptIn"
        :field-name="`${fieldNamePrefix}mailOptIn`"
        label="Keep me updated on new drops and exclusive offers"
        @input="onMailOptInChange"
      />

      <tml-input
        v-model="addressInfo.first_name"
        :field-name="`${fieldNamePrefix}first_name`"
        placeholder="First Name"
        type="text"
        :validation-rules="{required: true, max_length: 20}"
        @blur="onBlur"
      />

      <tml-input
        v-model="addressInfo.last_name"
        :field-name="`${fieldNamePrefix}last_name`"
        placeholder="Surname"
        type="text"
        :validation-rules="{required: true, max_length: 20}"
        @blur="onBlur"
      />

      <tml-input
        v-if="actAsBilling"
        v-model="addressInfo.phone"
        :field-name="`${fieldNamePrefix}phone`"
        placeholder="Phone"
        type="tel"
        class=""
        @blur="onBlur"
      />

      <template v-if="actAsBilling && showBusinessFields">
        <tml-input
          v-model="addressInfo.company"
          :field-name="`${fieldNamePrefix}company`"
          placeholder="Company (optional)"
          type="text"
          :validation-rules="{max_length: 100}"
          @blur="onBlur"
        />

        <tml-input
          v-model="addressInfo.vat_number"
          :field-name="`${fieldNamePrefix}vat_number`"
          placeholder="VAT number (optional)"
          type="text"
          :validation-rules="{vat: true}"
          @blur="onBlur"
        />
      </template>
    </div>
    <h5 v-if="actAsBilling">
      Billing address
    </h5>
    <template v-if="showLookup">
      <h5>Address finder</h5>

      <tml-autocomplete
        v-model="autocompleteQuery"
        :options="addresses"
        placeholder="Start typing the first line of your address"
        label="Keep typing your address to display more results"
        :form-name-prefix="fieldNamePrefix"
        :field-name-prefix="fieldNamePrefix"
        @selected="setPrediction"
      >
        <template #options-custom="{onSelect, allItems, select}">
          <div
            v-for="option in allItems"
            :key="option.uid"
            class="option flex justify-start items-center w-full cursor-pointer"
            style="padding: 0.8em"
            @click.stop="$event => onSelect(option, select)"
          >
            <p>
              {{ option.Text }} <small>{{ option.Description }}</small>
            </p>
          </div>
        </template>
      </tml-autocomplete>
      <tml-anchor
        v-if="!internalIsEditable && hideAddressCard"
        @click="toggleEditableAddress"
      >
        <p class="cursor-pointer">
          <small>
            Enter manually
            <font-awesome-icon :icon="faPencil" class="ml-1" />
          </small>
        </p>
      </tml-anchor>
    </template>

    <div v-show="!internalIsEditable && !hideAddressCard">
      <tml-card class="address-card">
        <tml-address :address="formattedAddressInfo" />

        <tml-anchor
          href="#"
          :icon="faEdit"
          text="Edit"
          class="edit"
          @click="toggleEditableAddress"
        />
      </tml-card>
    </div>

    <div v-show="internalIsEditable">
      <template v-if="splitStreetAddress">
        <tml-input
          v-model="houseNumber"
          :field-name="`${fieldNamePrefix}house_number`"
          placeholder="House Number or Name"
          type="text"
          :auto-validate="false"
          :validation-rules="{required: true, max_length: 20}"
          @blur="onSplitStreetBlur"
        />

        <tml-input
          v-model="streetName"
          :field-name="`${fieldNamePrefix}street_name`"
          placeholder="Street Address"
          type="text"
          :auto-validate="false"
          :validation-rules="{required: true, max_length: 84}"
          @blur="onSplitStreetBlur"
        />
      </template>

      <tml-input
        v-else
        v-model="addressInfo.street_address"
        :field-name="`${fieldNamePrefix}street_address`"
        placeholder="House Number and Street"
        type="text"
        :validation-rules="{required: true, max_length: 105}"
        @blur="onBlur"
      />

      <tml-input
        v-model="addressInfo.city"
        :field-name="`${fieldNamePrefix}city`"
        placeholder="Town/City"
        type="text"
        :validation-rules="{required: true, max_length: 20}"
        @blur="onBlur"
      />

      <tml-input
        v-model="addressInfo.zip"
        :field-name="`${fieldNamePrefix}zip`"
        placeholder="Postcode"
        type="text"
        :validation-rules="{required: true, max_length: 10}"
        @blur="onBlur"
      />

      <tml-input
        v-if="allowEori"
        v-show="showEori"
        v-model="addressInfo.eori_number"
        field-name="eori_number"
        placeholder="EORI number (optional)"
      />

      <tml-new-select
        v-model="selectedCountry"
        :name="`${fieldNamePrefix}country`"
        label="Country"
        :options="countryOptions"
        class="mb-4"
        searchable
        :searchable-properties="{
          text: 1,
        }"
        :validation-rules="{required: true}"
        :disabled="disableCountry"
        focus-on-open
      />

      <div v-show="stateIsRequired">
        <tml-new-select
          v-model="selectedState"
          :name="`${fieldNamePrefix}state`"
          label="County/State"
          :options="filteredStateOptions"
          class="mb-4"
          searchable
          :searchable-properties="{
            text: 1,
          }"
          :validation-rules="{required: stateIsRequired}"
          focus-on-open
        />
      </div>
    </div>
  </div>
</template>
<script>
import {faEdit, faPencil} from '@fortawesome/pro-light-svg-icons';
import {isObject, cloneDeep, isEmpty} from 'lodash';
import {formatUrl} from '@teemill/common/helpers';

export default {
  name: 'AddressInfo',

  inject: ['theme'],

  props: {
    addressInfo: Object,

    actAsBilling: {
      type: Boolean,
      default: true,
    },

    showMailOptIn: {
      type: Boolean,
      default: false,
    },

    countryOptions: Array,

    stateOptions: Array,

    lockCustomer: {
      type: Boolean,
      default: false,
    },

    isEditable: {
      type: Boolean,
      default: false,
    },

    showLookup: {
      type: Boolean,
      default: false,
    },

    allowEori: {
      type: Boolean,
      default: false,
    },

    disableCountry: {
      type: Boolean,
      default: false,
    },

    showBusinessFields: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      faEdit,
      faPencil,

      selectedCountryValue: null,
      selectedStateValue: null,
      alteredAddressInfo: false,
      ogAddressInfo: {},
      loading: false,
      timeout: false,

      autocompleteQuery: '',
      addresses: [],
      findAddressRunning: null,
      preventEmit: false,
      internalIsEditable: true,
      hideAddressCard: true,
      houseNumber: '',
      streetName: '',
    };
  },
  computed: {
    splitStreetAddress() {
      return this.theme().get('checkout.address.splitStreetAddress') === '1';
    },

    fieldNamePrefix() {
      return this.actAsBilling ? 'billing_' : 'shipping_';
    },

    showEori() {
      if (this.addressInfo.country === 'GB') {
        this.addressInfo.eori_number = null;

        return false;
      }

      return this.addressInfo.act_as_billing_info
        ? this.addressInfo.billing_is_shipping
        : !this.addressInfo.billing_is_shipping;
    },

    selectedCountry: {
      set(option) {
        if (isObject(option)) {
          this.selectedCountryValue = option.value;
        } else {
          this.selectedCountryValue = option;
        }
      },
      get() {
        return (
          this.countryOptions.find(
            o => o.value === this.selectedCountryValue
          ) || null
        );
      },
    },

    selectedState: {
      set(option) {
        if (isObject(option)) {
          this.selectedStateValue = option.value;
        } else {
          this.selectedStateValue = option;
        }
      },
      get() {
        return (
          this.stateOptions.find(o => o.value === this.selectedStateValue) ||
          null
        );
      },
    },

    filteredStateOptions() {
      return this.stateOptions.filter(so => this.filterStateCondition(so));
    },

    stateIsRequired() {
      return !!this.filteredStateOptions.length;
    },

    formattedAddressInfo() {
      const stateObject = this.stateOptions.find(
        o => o.value === this.addressInfo.state
      );
      const countryObject = this.countryOptions.find(
        o => o.value === this.addressInfo.country
      );

      const formattedInfo = {
        streetAddress: this.addressInfo.street_address,
        city: this.addressInfo.city,
        zip: this.addressInfo.zip,
        company: this.addressInfo.company,
        vatNumber: this.addressInfo.vat_number,
      };

      if (stateObject) {
        formattedInfo.state = {
          name: stateObject.text,
        };
      }

      if (countryObject) {
        formattedInfo.country = {
          name: countryObject.text,
        };
      }

      return formattedInfo;
    },
  },
  watch: {
    addressInfo: {
      immediate: true,
      deep: true,
      handler(value) {
        this.selectedState = value.state;
        this.selectedCountry = value.country;

        this.alteredAddressInfo =
          JSON.stringify(value) !== JSON.stringify(this.ogAddressInfo);
        if (!value.street_address) {
          this.hideAddressCard = true;
        } else {
          this.hideAddressCard = false;
        }

        if (this.splitStreetAddress && value.street_address?.includes('\n')) {
          [this.houseNumber, this.streetName] = value.street_address.split(
            '\n',
            2
          );
        }
      },
    },
    selectedCountryValue: {
      handler(value) {
        if (value !== this.addressInfo.country && !this.preventEmit) {
          this.selectedStateValue = null;
          this.$emit('update-country', value);
        }
      },
    },
    selectedStateValue: {
      handler(value) {
        if (value !== this.addressInfo.state && !this.preventEmit) {
          this.$emit('update-state', value);
        }
      },
    },
    autocompleteQuery: {
      immediate: true,
      handler(value, oldValue) {
        if (value && value !== oldValue) {
          this.restrictedFindAddress();
        }
      },
    },
    internalIsEditable: {
      immediate: true,
      handler(value, oldValue) {
        if (value !== oldValue) {
          this.$emit('editable-change', value);
        }
      },
    },
    isEditable: {
      immediate: true,
      handler(value, oldValue) {
        if (value !== oldValue) {
          this.internalIsEditable = value;
        }
      },
    },
    houseNumber() {
      if (this.splitStreetAddress) {
        this.syncStreetAddressFromSplit();
      }
    },
    streetName() {
      if (this.splitStreetAddress) {
        this.syncStreetAddressFromSplit();
      }
    },
  },
  mounted() {
    this.ogAddressInfo = cloneDeep(this.addressInfo);
  },
  methods: {
    isEmpty,
    syncStreetAddressFromSplit() {
      this.addressInfo.street_address = [
        this.houseNumber.trim(),
        this.streetName.trim(),
      ]
        .filter(Boolean)
        .join('\n');
    },

    onSplitStreetBlur() {
      this.syncStreetAddressFromSplit();
      this.onBlur();
    },

    onBlur() {
      if (this.alteredAddressInfo) {
        // Timeout is used to prevent autofill from emitting multiple times
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.$emit('update');

          this.alteredAddressInfo = false;

          this.ogAddressInfo = cloneDeep(this.addressInfo);
        });
      }
    },
    onMailOptInChange(value) {
      this.$emit('update:mail-opt-in', value);
    },
    filterStateCondition(so) {
      if (!this.selectedCountryValue) {
        return false;
      }

      const countryOption = this.countryOptions.find(
        co => co.value === this.selectedCountryValue
      );

      if (!countryOption) {
        return false;
      }

      if (so.country_id === countryOption.id) {
        return true;
      }

      return false;
    },

    setPrediction(prediction) {
      if (prediction.Type !== 'Address') {
        this.findAddress(prediction.Id);
        return;
      }

      this.retrieveAddress(prediction.Id);
    },

    restrictedFindAddress() {
      if (this.findAddressRunning) {
        clearTimeout(this.findAddressRunning);
        this.findAddressRunning = null;
      }

      this.findAddressRunning = setTimeout(() => {
        this.findAddress();
      }, 150);
    },

    findAddress(prediction) {
      if (!this.autocompleteQuery) {
        return;
      }

      this.axios
        .post(formatUrl('/omnis/v3/address-verification/find/'), {
          query: this.autocompleteQuery,
          container: typeof prediction !== 'undefined' ? prediction : '',
        })
        .then(({data}) => {
          if (data) {
            // add preventClose to each item that isn't an address
            data.forEach(item => {
              if (item.Type !== 'Address') {
                item.preventClose = true;
              }
            });

            this.addresses = data;
          }
        })
        .catch(() => {
          this.showErrorSnackBar();
        });
    },

    retrieveAddress(id) {
      this.axios
        .post(formatUrl('/omnis/v3/address-verification/retrieve/'), {
          id,
        })
        .then(({data}) => {
          if (data) {
            this.handleRetrieveResponse(data[0]);
          }
        })
        .catch(() => {
          this.showErrorSnackBar();
        });

      this.resetAutocomplete();
      this.internalIsEditable = false;
    },

    handleRetrieveResponse(data) {
      this.addressInfo.country = data.CountryIso2;
      const stateValue = this.stateOptions.find(state =>
        state.value.match(data.ProvinceName)
      );

      if (stateValue) {
        this.addressInfo.state = stateValue.value;
      } else {
        this.addressInfo.state = null;
      }

      const addressLines = [
        data.Line1,
        data.Line2,
        data.Line3,
        data.Line4,
        data.Line5,
      ];

      this.addressInfo.street_address = addressLines.filter(Boolean).join(', ');

      this.addressInfo.city = data.City;
      this.addressInfo.zip = data.PostalCode;

      this.$emit('force-update');

      this.preventEmit = true;
      this.selectedCountryValue = data.CountryIso2;
      if (stateValue) {
        this.selectedStateValue = stateValue.value;
      } else {
        this.selectedStateValue = null;
      }

      this.preventEmit = false;
    },

    toggleEditableAddress() {
      this.internalIsEditable = !this.internalIsEditable;
    },

    resetAutocomplete() {
      this.autocompleteQuery = '';
    },

    showErrorSnackBar() {
      this.$dialogs.showSnackBar({
        type: 'error',
        message:
          'Sorry, something went wrong, please enter your address manually.',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.address-card {
  .edit {
    position: absolute;
    cursor: pointer;
    bottom: 20px;
    right: 20px;
  }
}
</style>
